import {
  THealthIncident,
  TSafetyProtocol,
  TEmergencyContact,
  THealthCheckup,
} from 'src/apis/administration/administration.interfaces';
import {
  TDocumentStatus,
  TDocumentType,
} from 'src/apis/documents/documents.interfaces';

export const mockHealthIncidents: THealthIncident[] = [
  {
    id: 'incident-001',
    incidentType: 'INJURY',
    severity: 'MEDIUM',
    reportedBy: 'staff-001',
    reportedDate: new Date('2024-02-25'),
    incidentDate: new Date('2024-02-25'),
    location: 'Sports Ground',
    description: 'Student fell during cricket practice and injured ankle',
    affectedPersonType: 'STUDENT',
    affectedPersonId: 'student-001',
    symptoms: 'Swelling, pain in right ankle, difficulty walking',
    firstAidProvided: true,
    medicalAttentionRequired: true,
    hospitalVisited: 'City Hospital',
    followUpRequired: true,
    followUpDate: new Date('2024-03-01'),
    status: 'IN_PROGRESS',
    resolution: 'Student advised rest and physiotherapy',
    preventiveMeasures:
      'Better supervision during sports activities, proper warm-up exercises',
  },
  {
    id: 'incident-002',
    incidentType: 'ALLERGY',
    severity: 'LOW',
    reportedBy: 'staff-002',
    reportedDate: new Date('2024-02-20'),
    incidentDate: new Date('2024-02-20'),
    location: 'Science Lab',
    description: 'Student developed skin rash after chemistry experiment',
    affectedPersonType: 'STUDENT',
    affectedPersonId: 'student-015',
    symptoms: 'Red rash on hands, mild itching',
    firstAidProvided: true,
    medicalAttentionRequired: false,
    followUpRequired: false,
    status: 'RESOLVED',
    resolution: 'Rash subsided after applying calamine lotion',
    preventiveMeasures:
      'Use of protective gloves during experiments, proper safety briefing',
  },
  {
    id: 'incident-003',
    incidentType: 'ILLNESS',
    severity: 'HIGH',
    reportedBy: 'staff-003',
    reportedDate: new Date('2024-02-15'),
    incidentDate: new Date('2024-02-15'),
    location: 'Classroom 5A',
    description:
      'Student fainted during morning assembly due to low blood sugar',
    affectedPersonType: 'STUDENT',
    affectedPersonId: 'student-008',
    symptoms: 'Dizziness, sweating, loss of consciousness',
    firstAidProvided: true,
    medicalAttentionRequired: true,
    hospitalVisited: 'School Medical Room',
    followUpRequired: true,
    followUpDate: new Date('2024-02-22'),
    status: 'RESOLVED',
    resolution: 'Student recovered after glucose administration',
    preventiveMeasures:
      'Regular health checkups, awareness about diabetes management',
  },
  {
    id: 'incident-004',
    incidentType: 'ACCIDENT',
    severity: 'MEDIUM',
    reportedBy: 'staff-004',
    reportedDate: new Date('2024-02-10'),
    incidentDate: new Date('2024-02-10'),
    location: 'School Bus',
    description: 'Minor collision with another vehicle during school pickup',
    affectedPersonType: 'STAFF',
    affectedPersonId: 'driver-001',
    symptoms: 'Minor bruising, shock',
    firstAidProvided: true,
    medicalAttentionRequired: false,
    followUpRequired: false,
    status: 'CLOSED',
    resolution: 'Driver resumed duty after rest',
    preventiveMeasures:
      'Defensive driving training, regular vehicle maintenance',
  },
];

export const mockSafetyProtocols: TSafetyProtocol[] = [
  {
    id: 'protocol-001',
    protocolName: 'Fire Safety and Evacuation',
    category: 'FIRE_SAFETY',
    description:
      'Comprehensive fire safety procedures and emergency evacuation plan',
    procedures: [
      'Regular fire drills every month',
      'Designated assembly points for each class',
      'Fire extinguisher locations and usage training',
      'Emergency contact numbers prominently displayed',
      'Evacuation routes clearly marked',
    ],
    responsiblePerson: 'staff-005',
    lastUpdated: new Date('2024-01-15'),
    nextReviewDate: new Date('2024-07-15'),
    isActive: true,
    attachments: ['fire-safety-manual.pdf', 'evacuation-map.pdf'],
    trainingRequired: true,
    lastTrainingDate: new Date('2024-01-10'),
    nextTrainingDate: new Date('2024-07-10'),
  },
  {
    id: 'protocol-002',
    protocolName: 'First Aid Response',
    category: 'FIRST_AID',
    description: 'Standard first aid procedures for common school injuries',
    procedures: [
      'First aid kit available in every classroom',
      'Trained first aid responders in each department',
      'Emergency medical contact procedures',
      'Documentation of all first aid incidents',
      'Regular first aid training for staff',
    ],
    responsiblePerson: 'staff-006',
    lastUpdated: new Date('2024-02-01'),
    nextReviewDate: new Date('2024-08-01'),
    isActive: true,
    attachments: ['first-aid-manual.pdf', 'emergency-contacts.pdf'],
    trainingRequired: true,
    lastTrainingDate: new Date('2024-01-20'),
    nextTrainingDate: new Date('2024-07-20'),
  },
  {
    id: 'protocol-003',
    protocolName: 'Food Safety in Canteen',
    category: 'FOOD_SAFETY',
    description:
      'Food safety standards and hygiene practices for school canteen',
    procedures: [
      'Daily food quality checks',
      'Proper food storage and temperature control',
      'Regular kitchen hygiene audits',
      'Staff health checkups and food handling training',
      'Allergen information prominently displayed',
    ],
    responsiblePerson: 'staff-007',
    lastUpdated: new Date('2024-01-20'),
    nextReviewDate: new Date('2024-07-20'),
    isActive: true,
    attachments: ['food-safety-guidelines.pdf', 'hygiene-checklist.pdf'],
    trainingRequired: true,
    lastTrainingDate: new Date('2024-01-25'),
    nextTrainingDate: new Date('2024-07-25'),
  },
  {
    id: 'protocol-004',
    protocolName: 'Transport Safety',
    category: 'TRANSPORT_SAFETY',
    description: 'Safety protocols for school transportation',
    procedures: [
      'Regular vehicle maintenance checks',
      'Driver qualification and health verification',
      'Student safety briefing before each trip',
      'Emergency procedures during transit',
      'GPS tracking and communication systems',
    ],
    responsiblePerson: 'staff-008',
    lastUpdated: new Date('2024-02-05'),
    nextReviewDate: new Date('2024-08-05'),
    isActive: true,
    attachments: ['transport-safety-manual.pdf', 'vehicle-checklist.pdf'],
    trainingRequired: true,
    lastTrainingDate: new Date('2024-01-30'),
    nextTrainingDate: new Date('2024-07-30'),
  },
];

export const mockEmergencyContacts: TEmergencyContact[] = [
  {
    id: 'contact-001',
    name: 'Dr. Rajesh Kumar',
    relationship: 'DOCTOR',
    phoneNumber: '+91-80-12345678',
    alternatePhoneNumber: '+91-9880123456',
    email: 'dr.rajesh@cityhospital.com',
    address: 'City Hospital, MG Road, Bangalore',
    isActive: true,
    priority: 1,
    personId: 'student-001',
    personType: 'STUDENT',
  },
  {
    id: 'contact-002',
    name: 'Smt. Lakshmi Devi',
    relationship: 'PARENT',
    phoneNumber: '+91-9881234567',
    alternatePhoneNumber: '+91-80-87654321',
    email: 'lakshmi.devi@email.com',
    address: '123, Vijayanagar 4th Stage, Bangalore',
    isActive: true,
    priority: 1,
    personId: 'student-001',
    personType: 'STUDENT',
  },
  {
    id: 'contact-003',
    name: 'Sri. Mohan Kumar',
    relationship: 'GUARDIAN',
    phoneNumber: '+91-9882345678',
    alternatePhoneNumber: '+91-80-76543210',
    email: 'mohan.kumar@email.com',
    address: '456, Kengeri, Bangalore',
    isActive: true,
    priority: 2,
    personId: 'student-004',
    personType: 'STUDENT',
  },
  {
    id: 'contact-004',
    name: 'Apollo Hospital',
    relationship: 'HOSPITAL',
    phoneNumber: '+91-80-11223344',
    alternatePhoneNumber: '+91-80-22334455',
    email: 'emergency@apollohospital.com',
    address: 'Apollo Hospital, Bannerghatta Road, Bangalore',
    isActive: true,
    priority: 1,
    personId: 'school-general',
    personType: 'STAFF',
  },
  {
    id: 'contact-005',
    name: 'Police Control Room',
    relationship: 'EMERGENCY_CONTACT',
    phoneNumber: '100',
    alternatePhoneNumber: '+91-80-22942222',
    email: 'control@karnatakapolice.gov.in',
    address: 'Police Commissioner Office, Infantry Road, Bangalore',
    isActive: true,
    priority: 1,
    personId: 'school-general',
    personType: 'STAFF',
  },
];

export const mockHealthCheckups: THealthCheckup[] = [
  {
    id: 'checkup-001',
    personId: 'student-001',
    personType: 'STUDENT',
    checkupDate: new Date('2024-01-15'),
    checkupType: 'ANNUAL',
    doctorName: 'Dr. Priya Sharma',
    hospitalClinic: 'School Health Center',
    findings: 'Normal health, BMI 18.5, vision 20/20, hearing normal',
    recommendations: 'Continue regular exercise, maintain balanced diet',
    nextCheckupDate: new Date('2025-01-15'),
    isCleared: true,
    notes: 'Student is fit for all school activities',
    attachments: [
      {
        documentId: 'document-001',
        documentName: 'health-report-2024.pdf',
        documentType: TDocumentType.OTHER,
        submissionDate: '2024-01-15',
        status: TDocumentStatus.VERIFIED,
        uploadedAt: { userId: 'user-001', timestamp: Date.now() },
        userId: '123123',
      },
    ],
  },
  {
    id: 'checkup-002',
    personId: 'student-008',
    personType: 'STUDENT',
    checkupDate: new Date('2024-02-20'),
    checkupType: 'MEDICAL',
    doctorName: 'Dr. Amit Patel',
    hospitalClinic: 'City Hospital',
    findings: 'Low blood sugar levels, needs monitoring',
    recommendations:
      'Regular blood sugar monitoring, balanced meals, carry glucose tablets',
    nextCheckupDate: new Date('2024-05-20'),
    isCleared: true,
    notes: 'Student can attend school with proper monitoring',
    attachments: [
      {
        documentId: 'document-001',
        documentName: 'diabetes-report.pdf',
        documentType: TDocumentType.MEDICAL_CERTIFICATE,
        submissionDate: '2024-01-15',
        status: TDocumentStatus.VERIFIED,
        uploadedAt: { userId: 'user-001', timestamp: Date.now() },
        userId: '123123',
      },
      {
        documentId: 'document-002',
        documentName: 'diet-chart.pdf',
        documentType: TDocumentType.OTHER,
        submissionDate: '2024-01-15',
        status: TDocumentStatus.VERIFIED,
        uploadedAt: { userId: 'user-001', timestamp: Date.now() },
        userId: '123123',
      },
    ],
  },
  {
    id: 'checkup-003',
    personId: 'staff-001',
    personType: 'STAFF',
    checkupDate: new Date('2024-01-10'),
    checkupType: 'ANNUAL',
    doctorName: 'Dr. Sunita Reddy',
    hospitalClinic: 'Staff Health Center',
    findings: 'Good health, blood pressure normal, no major issues',
    recommendations: 'Continue regular exercise, stress management',
    nextCheckupDate: new Date('2025-01-10'),
    isCleared: true,
    notes: 'Staff member is fit for all duties',
    attachments: [
      {
        documentId: 'document-001',
        documentName: 'staff-health-report-2024.pdf',
        documentType: TDocumentType.OTHER,
        submissionDate: '2024-01-15',
        status: TDocumentStatus.VERIFIED,
        uploadedAt: { userId: 'user-001', timestamp: Date.now() },
        userId: '123123',
      },
    ],
  },
  {
    id: 'checkup-004',
    personId: 'driver-001',
    personType: 'STAFF',
    checkupDate: new Date('2024-02-01'),
    checkupType: 'MEDICAL',
    doctorName: 'Dr. Ramesh Kumar',
    hospitalClinic: 'Transport Medical Center',
    findings: 'Vision 20/40, needs corrective lenses for driving',
    recommendations:
      'Wear prescribed glasses while driving, regular eye checkups',
    nextCheckupDate: new Date('2024-08-01'),
    isCleared: true,
    notes: 'Driver can continue with corrective lenses',
    attachments: [
      {
        documentId: 'document-001',
        documentName: 'diabetes-report.pdf',
        documentType: TDocumentType.MEDICAL_CERTIFICATE,
        submissionDate: '2024-01-15',
        status: TDocumentStatus.VERIFIED,
        uploadedAt: { userId: 'user-001', timestamp: Date.now() },
        userId: '123123',
      },
      {
        documentId: 'document-002',
        documentName: 'diet-chart.pdf',
        documentType: TDocumentType.OTHER,
        submissionDate: '2024-01-15',
        status: TDocumentStatus.VERIFIED,
        uploadedAt: { userId: 'user-001', timestamp: Date.now() },
        userId: '123123',
      },
    ],
  },
];
